/**
 * Zużycie LLM i dzienne budżety płatnych API (Faza 8).
 *
 * Licznik jest po stronie backendu (klucz `llm_usage:<data>` w ustawieniach);
 * tutaj tylko go pokazujemy: dziś + 7 dni, per provider i model.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { settingsKeys } from "./SettingsPage";
import type { LlmUsage } from "./types";

function formatTokens(value: number): string {
  if (value < 1000) return String(value);
  return `${(value / 1000).toFixed(1)}k`;
}

/** Model widziany w UI: wg preferowanego providera dla danego typu zadania. */
function activeModel(data: LlmUsage, task: "converse" | "review"): string {
  const provider = data.providers[task];
  if (provider === "anthropic") {
    return task === "converse" ? data.models.anthropic_converse : data.models.anthropic_review;
  }
  if (provider === "openai") return data.models[task];
  return data.models.local;
}

function formatDay(iso: string): string {
  const at = new Date(`${iso}T00:00:00`);
  return Number.isNaN(at.getTime())
    ? iso
    : at.toLocaleDateString("pl-PL", { weekday: "short", day: "numeric", month: "short" });
}

export function LlmUsageSection() {
  const { data, isLoading, error } = useQuery({
    queryKey: settingsKeys.llmUsage,
    queryFn: () => api.llmUsage(7),
  });

  return (
    <section className="card settings__section">
      <h2 className="card__title">Zużycie modelu</h2>
      <p className="muted">
        Liczba wywołań i tokenów per dzień. Po przekroczeniu dziennego budżetu router przestaje
        wołać danego dostawcę — zostaje kolejny model z łańcucha, a ceremonie tekst zapasowy.
      </p>

      {isLoading && <p className="muted">Ładowanie…</p>}
      {error && <p className="error">Nie udało się pobrać zużycia.</p>}

      {data && (
        <>
          <ul className="rows">
            <li className="row">
              <div className="row__main">
                <span className="row__title">Budżet Claude (dziś)</span>
                <span className="row__meta">
                  {!data.anthropic_configured
                    ? "brak klucza — Claude wyłączony"
                    : data.anthropic_budget.unlimited
                      ? `${data.anthropic_budget.used} wywołań, bez limitu`
                      : `${data.anthropic_budget.used} / ${data.anthropic_budget.limit} wywołań`}
                  {data.anthropic_budget.exhausted && (
                    <span className="badge badge--warn">budżet wyczerpany</span>
                  )}
                </span>
              </div>
            </li>
            <li className="row">
              <div className="row__main">
                <span className="row__title">Budżet OpenAI (dziś)</span>
                <span className="row__meta">
                  {!data.openai_configured
                    ? "brak klucza — fallback wyłączony"
                    : data.budget.unlimited
                      ? `${data.budget.used} wywołań, bez limitu`
                      : `${data.budget.used} / ${data.budget.limit} wywołań`}
                  {data.budget.exhausted && (
                    <span className="badge badge--warn">budżet wyczerpany</span>
                  )}
                </span>
              </div>
            </li>
            <li className="row">
              <div className="row__main">
                <span className="row__title">Modele</span>
                <span className="row__meta">
                  chat: <code>{activeModel(data, "converse")}</code> · przegląd:{" "}
                  <code>{activeModel(data, "review")}</code> · lokalny:{" "}
                  <code>{data.models.local}</code>
                </span>
              </div>
            </li>
          </ul>

          <ul className="rows">
            {data.days.map((day) => (
              <li className="row" key={day.date}>
                <div className="row__main">
                  <span className="row__title">
                    {formatDay(day.date)} <span className="group__count">{day.requests}</span>
                  </span>
                  <span className="row__meta">
                    {day.requests === 0
                      ? "brak wywołań"
                      : `${formatTokens(day.prompt_tokens)} in / ${formatTokens(
                          day.completion_tokens,
                        )} out`}
                    {day.entries.map((entry) => (
                      <span key={`${entry.provider}/${entry.model}`}>
                        {entry.provider}/{entry.model}: {entry.requests}
                      </span>
                    ))}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
